import { addMonths } from 'date-fns';
import { UTCDate } from '@date-fns/utc';
import type { Sport } from '@sharpr/types';

const SEASON_END_ZONE = 'America/Los_Angeles';

/**
 * End (ms) of the monthly billing cycle that starts at `fromMs`, honoring Stripe's
 * billing-cycle anchor behavior: a cycle advances one calendar month and RETURNS to the
 * anchor day-of-month (Jan 31 → Feb 28 → Mar 31), clamping only for short months. Plain
 * `Date.setUTCMonth(+1)` overflows (Jan 31 → Mar 3), so the tail-window classifier and the
 * off-season pause predicate MUST both route through this to agree on the boundary —
 * otherwise an end-of-month signup can be $0-tail-classified while the pause predicate would
 * have billed a full month (divergent billing on a real boundary).
 */
export function nextCycleBoundaryMs(fromMs: number, anchorDay: number): number {
  const next = addMonths(new UTCDate(fromMs), 1);
  const daysInNextMonth = new UTCDate(
    next.getUTCFullYear(), next.getUTCMonth() + 1, 0
  ).getUTCDate();
  next.setUTCDate(Math.min(anchorDay, daysInNextMonth));
  return next.getTime();
}

// Shared parse+resolve for the two season DATE boundaries → the UTC instant at PT midnight
// of (date + dayOffset). End boundary uses dayOffset=1 (start of the following day = the
// access cutoff); start boundary uses dayOffset=0. Throws on malformed/out-of-range input
// (both columns are NOT NULL DATE, so malformed = an upstream bug).
function ptMidnightBoundary(
  dateStr: string,
  dayOffset: 0 | 1,
  fnLabel: string,
  fieldLabel: string,
): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(dateStr);
  if (!match) {
    throw new Error(`[${fnLabel}] invalid ${fieldLabel}=${dateStr}; expected YYYY-MM-DD`);
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  // Reject out-of-range components (Date.UTC silently rolls 2025-13-01 over).
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    throw new Error(`[${fnLabel}] invalid ${fieldLabel}=${dateStr}; out-of-range`);
  }
  const naiveUtcMs = Date.UTC(year, month - 1, day + dayOffset);
  const offsetMin = ptOffsetMinutesAt(new Date(naiveUtcMs));
  return new Date(naiveUtcMs - offsetMin * 60_000);
}

/**
 * Resolve `sport_seasons.end_date` (DATE) to the UTC moment at the START of the FOLLOWING
 * day in Pacific Time — the cutoff after which final-day access ends. Drives the
 * off-season pause/resume timing and the in-season / last-day signup guards.
 *
 * PT (not ET/UTC) because the product serves all US zones: West Coast late games run past
 * midnight ET, so PT next-day midnight covers every user; ET cuts West Coast mid-game and
 * naive UTC (the prior bug) cuts everyone 4-5h early. DST-aware via Intl. Throws on
 * malformed input (end_date is NOT NULL DATE → malformed = upstream bug).
 */
export function seasonEndBoundary(endDate: string): Date {
  return ptMidnightBoundary(endDate, 1, 'seasonEndBoundary', 'endDate');
}

/**
 * Resolve `sport_seasons.start_date` (DATE) to the UTC moment at the START of that day in
 * Pacific Time — the season-start instant. Symmetric with seasonEndBoundary so resume/notice
 * timing isn't a calendar day early for non-UTC viewers (a bare `new Date(start_date)` is
 * UTC-midnight ≈ 5pm PT the PRIOR day, which would resume + bill month-one a day early).
 * Throws on malformed input (start_date is NOT NULL DATE → malformed = upstream bug).
 */
export function seasonStartBoundary(startDate: string): Date {
  return ptMidnightBoundary(startDate, 0, 'seasonStartBoundary', 'startDate');
}

function ptOffsetMinutesAt(d: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SEASON_END_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const wallUtcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second')
  );
  return Math.round((wallUtcMs - d.getTime()) / 60_000);
}

/** How early before start_date to begin surfacing subscribe/activate CTAs — the pre-sale
 *  window. A pre-season signup's trial does not start until the season does
 *  (`coverageStartMsForCart`), so selling early costs the member none of their trial. */
export const SEASON_CTA_LEAD_DAYS = 14;

/** How early before start_date the CA-ARL pre-resume notice goes out to paused members
 *  (Terms/FAQ promise "at least 7 days"). Independent of the pre-sale window above. */
export const RESUME_NOTICE_LEAD_DAYS = 7;

/** Whether to surface subscribe/activate CTAs: true when in-season or starting within
 *  SEASON_CTA_LEAD_DAYS, false in the offseason. Missing season data fails open. */
export function isWithinSeasonCtaWindow(
  season: { start_date: string; end_date: string } | undefined,
  now: Date = new Date(),
  leadDays: number = SEASON_CTA_LEAD_DAYS
): boolean {
  if (!season) return true;
  const nowMs = now.getTime();
  if (nowMs > seasonEndBoundary(season.end_date).getTime()) return false;
  let startMs: number;
  try {
    startMs = seasonStartBoundary(season.start_date).getTime();
  } catch {
    return true; // malformed start_date → fail open (surface the CTA)
  }
  return nowMs >= startMs - leadDays * 86_400_000;
}

/**
 * The sport a no-URL-sport surface (login/OAuth redirect fallback, admin/settings selectors)
 * should default to: whichever is currently in season, NBA winning when BOTH — or NEITHER —
 * are. Single source of the tiebreak so the client hook and the server helper can't drift.
 * `isWithinSeasonCtaWindow` fails OPEN per-sport (missing row reads as in-season).
 */
export function pickInSeasonDefaultSport(
  seasons: ReadonlyArray<{ sport: Sport; start_date: string; end_date: string }>,
  now: Date = new Date(),
): Sport {
  const inSeason = (sport: Sport) =>
    isWithinSeasonCtaWindow(seasons.find((s) => s.sport === sport), now);
  return inSeason('nba') ? 'nba' : inSeason('mlb') ? 'mlb' : 'nba';
}

/**
 * The odds board to send an AUTHENTICATED user to when no explicit destination was
 * requested. Entitlement- AND liveness-aware (contrast `pickInSeasonDefaultSport`, which is
 * calendar-only + counts the pre-season lead — that's for no-user surfaces like the
 * admin/settings selectors):
 *   1. a HELD sport with LIVE games → that sport. A member is never pulled off their own
 *      live board (dual-sport, both live → NBA-first tiebreak).
 *   2. none of their sports are live but the OTHER sport is → that (live) sport. A live
 *      public odds board beats the member's empty off-season one, and both boards are
 *      public odds-only, so this exposes nothing paid.
 *   3. nothing is live anywhere → the member's own sport (calendar tiebreak if they hold
 *      both); no access at all → the pure-calendar default.
 * "Live" = within [start, end] with NO lead (leadDays=0), so a not-yet-started sport in its
 * pre-season lead never wins.
 */
export function pickDefaultSportForUser(
  sportsAccess: ReadonlyArray<Sport>,
  seasons: ReadonlyArray<{ sport: Sport; start_date: string; end_date: string }>,
  now: Date = new Date(),
): Sport {
  const SPORTS = ['nba', 'mlb'] as const; // iteration order = NBA-first tiebreak
  const isPlaying = (s: Sport) =>
    isWithinSeasonCtaWindow(seasons.find((x) => x.sport === s), now, 0);
  const held = SPORTS.filter((s) => sportsAccess.includes(s));

  const heldPlaying = held.filter(isPlaying);
  if (heldPlaying.length) return heldPlaying[0]; // 1. never pull off a live held board

  const anyPlaying = SPORTS.filter(isPlaying);
  if (anyPlaying.length) return anyPlaying[0]; // 2. their sport is dark → show the live one

  return held.length ? held[0] : pickInSeasonDefaultSport(seasons, now); // 3. nothing live
}

/** True when the sport is in its season TAIL: still in season, but less than one
 *  full billing cycle remains. Signups here are born paused (free rest-of-season,
 *  first charge at next season's resume) instead of paying a full month for a
 *  partial season. Uses the SHARED `nextCycleBoundaryMs` (anchor-clamped) so it
 *  agrees exactly with the pause predicate in `offseasonPause.ts` — a plain
 *  setUTCMonth(+1) here would overflow end-of-month (Jan 31 → Mar 3) and
 *  $0-classify signups the pause predicate would bill a full month. */
export function isSeasonTailWindow(
  season: { end_date: string } | undefined,
  nowMs: number,
): boolean {
  if (!season) return false;
  const boundaryMs = seasonEndBoundary(season.end_date).getTime();
  if (nowMs >= boundaryMs) return false;
  // A fresh signup's billing anchor is today, so its first cycle ends on today's
  // day-of-month next month (anchor-clamped).
  const anchorDay = new UTCDate(nowMs).getUTCDate();
  return nextCycleBoundaryMs(nowMs, anchorDay) > boundaryMs;
}

/** When a sport's coverage starts for a signup at `nowMs`: the season start when the season
 *  has not started yet (pre-sale), else now. A pre-sale member's trial runs its full length
 *  from opening night, and a member without a trial is first billed on opening night — not
 *  for weeks before there are games to bet. Missing or malformed season data → now. */
export function coverageStartMsForSport(
  season: { start_date: string } | undefined,
  nowMs: number,
): number {
  if (!season) return nowMs;
  try {
    return Math.max(nowMs, seasonStartBoundary(season.start_date).getTime());
  } catch {
    return nowMs;
  }
}

/** The opener a cart waits for, or null when its coverage starts now. One trial_end /
 *  one first-charge date per cart, taken from the EARLIEST start among its non-tail sports
 *  (tail = `isSeasonTailWindow`, the same classifier checkout uses). So a cart waits only
 *  when every such sport is still pre-season — a cart holding an in-season sport starts
 *  (trial or billing) today, never handing that sport free weeks. Pre-sale opens at most
 *  SEASON_CTA_LEAD_DAYS early, which keeps a deferred billing anchor inside the one-period
 *  limit Stripe places on a future billing_cycle_anchor. The single source for checkout,
 *  add-sport, pricing and the success page. */
export function pendingCoverageStartMs(
  sports: ReadonlyArray<Sport>,
  seasons: ReadonlyArray<{ sport: Sport; start_date: string; end_date: string }>,
  nowMs: number,
): number | null {
  const seasonOf = (s: Sport) => seasons.find((x) => x.sport === s);
  const starts = sports
    .filter((s) => !isSeasonTailWindow(seasonOf(s), nowMs))
    .map((s) => coverageStartMsForSport(seasonOf(s), nowMs));
  if (starts.length === 0) return null;
  const start = Math.min(...starts);
  return start > nowMs ? start : null;
}
